// src/pages/DownloadAnalytics.tsx
import React, { useEffect, useState, useMemo } from 'react';
import {
   Box,
   Typography,
   Paper,
   CircularProgress,
   Alert,
   Button,
   Card,
   CardContent,
   Chip,
   Dialog,
   DialogTitle,
   DialogContent,
   IconButton,
   ImageList,
   ImageListItem,
   useMediaQuery,
   useTheme,
   Snackbar,
   ToggleButton,
   ToggleButtonGroup,
   Avatar,
} from '@mui/material';
import {
   Download as DownloadIcon,
   Folder as FolderIcon,
   Image as ImageIcon,
   Close as CloseIcon,
   Person as PersonIcon,
   ViewModule as ViewModuleIcon,
} from '@mui/icons-material';
import { getAuthHeaders } from '../utils/auth';

interface Photo {
   id: string;
   url: string | null;
   thumbnailUrl: string | null;
   title: string;
   downloadedAt: any;
}

/** Photo with albumId for download tracking when shown in user-aggregated view */
export interface PhotoWithAlbumId extends Photo {
   albumId: string;
}

interface AlbumDownloads {
   albumId: string;
   albumName: string;
   albumDescription: string | null;
   albumCoverUrl: string | null;
   albumSource: string;
   isPublic: boolean;
   memberCount: number;
   createdBy: string | null;
   createdByName?: string;
   createdByPhotoURL?: string | null;
   photos: Photo[];
   photoCount: number;
}

interface MyDownloadsData {
   albums: AlbumDownloads[];
   totalDownloads: number;
}

interface UserDownloads {
   userId: string;
   displayName: string;
   photoURL: string | null;
   photoCount: number;
   photos: PhotoWithAlbumId[];
   albums: { albumId: string; albumName: string }[];
}

const DownloadAnalytics: React.FC = () => {
   const theme = useTheme();
   const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
   const isTablet = useMediaQuery(theme.breakpoints.down('md'));
   
   const [downloadsData, setDownloadsData] = useState<MyDownloadsData | null>(null);
   const [loading, setLoading] = useState(true);
   const [error, setError] = useState<string | null>(null);
   const [selectedAlbum, setSelectedAlbum] = useState<AlbumDownloads | null>(null);
   const [selectedUser, setSelectedUser] = useState<UserDownloads | null>(null);
   const [openModal, setOpenModal] = useState(false);
   const [viewMode, setViewMode] = useState<'person' | 'album'>('person');
   const [downloadingPhotoId, setDownloadingPhotoId] = useState<string | null>(null);
   const [snackbarOpen, setSnackbarOpen] = useState(false);
   const [snackbarMessage, setSnackbarMessage] = useState('');
   const [snackbarSeverity, setSnackbarSeverity] = useState<'success' | 'error'>('error');

   // Group albums by creator and aggregate photos across albums
   const userDownloads = useMemo((): UserDownloads[] => {
      if (!downloadsData?.albums?.length) return [];
      const byUser = new Map<string, { albums: AlbumDownloads[] }>();
      for (const album of downloadsData.albums) {
         const key = album.createdBy ?? 'unknown';
         if (!byUser.has(key)) byUser.set(key, { albums: [] });
         byUser.get(key)!.albums.push(album);
      }
      const result: UserDownloads[] = [];
      byUser.forEach((value, userId) => {
         const albums = value.albums;
         const first = albums[0];
         const displayName = first?.createdByName ?? 'Unknown';
         const photoURL = first?.createdByPhotoURL ?? null;
         const photosWithAlbum: PhotoWithAlbumId[] = [];
         const seen = new Set<string>();
         for (const album of albums) {
            for (const p of album.photos) {
               if (seen.has(p.id)) continue;
               seen.add(p.id);
               photosWithAlbum.push({ ...p, albumId: album.albumId });
            }
         }
         photosWithAlbum.sort((a, b) => {
            const tA = a.downloadedAt?.toMillis?.() ?? (a.downloadedAt?._seconds ?? 0) * 1000;
            const tB = b.downloadedAt?.toMillis?.() ?? (b.downloadedAt?._seconds ?? 0) * 1000;
            return tB - tA;
         });
         result.push({
            userId,
            displayName,
            photoURL,
            photoCount: photosWithAlbum.length,
            photos: photosWithAlbum,
            albums: albums.map((a) => ({ albumId: a.albumId, albumName: a.albumName })),
         });
      });
      result.sort((a, b) => b.photoCount - a.photoCount);
      return result;
   }, [downloadsData]);

   useEffect(() => {
      fetchMyDownloads();
   }, []);

   const fetchMyDownloads = async () => {
      try {
         setLoading(true);
         setError(null);

         const headers = await getAuthHeaders();
         const url = `${process.env.REACT_APP_API_URL}/api/albums/my-downloads`;
         const response = await fetch(url, { headers });

         if (!response.ok) {
            const errorText = await response.text();
            if (process.env.NODE_ENV === 'development') console.error('Response error:', errorText);
            let errorMessage = 'Failed to fetch downloads';
            try {
               const errorData = JSON.parse(errorText);
               errorMessage = errorData.message || errorMessage;
            } catch (e) {
               errorMessage = errorText || errorMessage;
            }
            throw new Error(errorMessage);
         }

         const data = await response.json();

         if (data.success === false) {
            throw new Error(data.message || 'Failed to fetch downloads');
         }
         
         setDownloadsData({
            albums: data.albums || [],
            totalDownloads: data.totalDownloads || 0,
         });
      } catch (err: any) {
         if (process.env.NODE_ENV === 'development') console.error('Error fetching downloads:', err);
         setError(err.message || 'Failed to load downloads');
      } finally {
         setLoading(false);
      }
   };

   const handleAlbumClick = (album: AlbumDownloads) => {
      setSelectedAlbum(album);
      setSelectedUser(null);
      setOpenModal(true);
   };

   const handleUserClick = (user: UserDownloads) => {
      setSelectedUser(user);
      setSelectedAlbum(null);
      setOpenModal(true);
   };

   const handleCloseModal = () => {
      setOpenModal(false);
      setSelectedAlbum(null);
      setSelectedUser(null);
   };

   const formatDate = (timestamp: any) => {
      if (!timestamp) return 'Unknown';
      try {
      const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp._seconds * 1000);
         return new Date(date).toLocaleDateString('en-US', { 
            month: 'short', 
            day: 'numeric', 
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
         });
      } catch {
         return 'Unknown';
      }
   };

   const getAlbumTypeLabel = (source: string) => {
      switch (source) {
         case 'trip':
            return 'Trip Album';
         case 'event':
            return 'Event Album';
         case 'manual':
         default:
            return 'Personal Album';
      }
   };

   const handleDownloadPhoto = async (photoId: string, photoTitle: string, albumId: string) => {
      try {
         setDownloadingPhotoId(photoId);

         const headers = await getAuthHeaders();
         
         // Track download activity in background (non-blocking)
         const trackDownload = async () => {
            try {
               const trackHeaders = await getAuthHeaders();
               const trackResponse = await fetch(
                  `${process.env.REACT_APP_API_URL}/api/albums/${albumId}/track-download`,
                  { 
                     method: 'POST',
                     headers: {
                        ...trackHeaders,
                        'Content-Type': 'application/json',
                     },
                     body: JSON.stringify({ photoId })
                  }
               );
               
               if (trackResponse.ok) {
                  await trackResponse.json();
               }
            } catch (error) {
               if (process.env.NODE_ENV === 'development') console.warn('Could not track download:', error);
            }
         };
         
         // Start tracking in background
         trackDownload();
         
         // Get signed download URL from backend
         const response = await fetch(
            `${process.env.REACT_APP_API_URL}/api/albums/${albumId}/photos/${photoId}/download`,
            {
               headers,
            }
         );

         if (!response.ok) {
            let errorMessage = 'Failed to download photo';
            try {
               const errorData = await response.json();
               errorMessage = errorData.message || errorMessage;
            } catch (e) {
               errorMessage = response.statusText || errorMessage;
            }
            throw new Error(errorMessage);
         }

         // Get filename from Content-Disposition header or use default
         const contentDisposition = response.headers.get('content-disposition');
         let fileName = `photo-${photoId}.jpg`;
         if (contentDisposition) {
            const match = contentDisposition.match(/filename="(.+)"/);
            if (match && match[1]) {
               fileName = match[1];
            }
         }

         // Get the blob from streamed response
         const blob = await response.blob();

         // Create temporary download link and trigger immediate download
         const blobUrl = URL.createObjectURL(blob);
         const link = document.createElement('a');
         link.href = blobUrl;
         link.download = fileName;
         link.style.display = 'none';
         
         // Trigger download immediately
         document.body.appendChild(link);
         link.click();
         
         // Clean up immediately
         document.body.removeChild(link);
         URL.revokeObjectURL(blobUrl);
      } catch (error: any) {
         setSnackbarMessage(error.message || 'Failed to download photo');
         setSnackbarSeverity('error');
         setSnackbarOpen(true);
      } finally {
         setDownloadingPhotoId(null);
      }
   };

   if (loading) {
      return (
         <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
            <CircularProgress />
         </Box>
      );
   }

   if (error || !downloadsData || downloadsData.albums.length === 0) {
      return (
         <Box sx={{ p: 3 }}>
            <Paper sx={{ p: 4, textAlign: 'center' }}>
               <DownloadIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
               <Typography variant="h6" gutterBottom>
                  No downloads yet
               </Typography>
               <Typography variant="body2" color="text.secondary" paragraph>
                  Photos you download from shared albums will appear here, grouped by person.
               </Typography>
               <Typography variant="body2" color="text.secondary">
                  Download photos from shared albums to see them here.
               </Typography>
            </Paper>
         </Box>
      );
   }

   const cardSx = {
      height: '100%' as const,
      display: 'flex',
      flexDirection: 'column' as const,
      cursor: 'pointer',
      transition: 'transform 0.2s ease, box-shadow 0.2s ease',
      borderRadius: 2,
      overflow: 'hidden',
      boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
      border: '1px solid',
      borderColor: 'divider',
      '&:hover': {
         transform: 'translateY(-4px)',
         boxShadow: 4,
      },
   };

   return (
      <Box sx={{ p: 3 }}>
         <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Photos you&apos;ve downloaded, grouped by who shared them.
         </Typography>

         <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
            <ToggleButtonGroup
               value={viewMode}
               exclusive
               onChange={(_, v) => v != null && setViewMode(v)}
               size="small"
            >
               <ToggleButton value="person" aria-label="By person">
                  <PersonIcon sx={{ mr: 0.5 }} /> By person
               </ToggleButton>
               <ToggleButton value="album" aria-label="By album">
                  <ViewModuleIcon sx={{ mr: 0.5 }} /> By album
               </ToggleButton>
            </ToggleButtonGroup>
         </Box>

         {/* Cards: by person or by album */}
         <Box
            sx={{
               display: 'flex',
               gap: 3,
               overflowX: 'auto',
               pb: 2,
               '&::-webkit-scrollbar': { height: 8 },
               '&::-webkit-scrollbar-track': { backgroundColor: 'rgba(0,0,0,0.05)', borderRadius: 4 },
               '&::-webkit-scrollbar-thumb': {
                  backgroundColor: 'rgba(0, 196, 167, 0.5)',
                  borderRadius: 4,
                  '&:hover': { backgroundColor: 'rgba(0, 196, 167, 0.7)' },
               },
            }}
         >
            {viewMode === 'person'
               ? userDownloads.map((user) => (
                  <Box key={user.userId} sx={{ minWidth: 300, maxWidth: 300, flexShrink: 0 }}>
                     <Card sx={cardSx} onClick={() => handleUserClick(user)}>
                        <Box
                           sx={{
                              height: 220,
                              display: 'grid',
                              gridTemplateColumns: '1fr 1fr',
                              gridTemplateRows: '1fr 1fr',
                              gap: 1,
                              bgcolor: 'grey.100',
                              p: 1,
                           }}
                        >
                           {user.photos.slice(0, 4).map((photo) => (
                              <Box
                                 key={photo.id}
                                 sx={{
                                    position: 'relative',
                                    width: '100%',
                                    height: '100%',
                                    overflow: 'hidden',
                                    borderRadius: 1.5,
                                 }}
                              >
                                 {photo.thumbnailUrl || photo.url ? (
                                    <img
                                       src={photo.thumbnailUrl || photo.url || ''}
                                       alt={photo.title}
                                       style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                                    />
                                 ) : (
                                    <Box
                                       sx={{
                                          width: '100%',
                                          height: '100%',
                                          display: 'flex',
                                          alignItems: 'center',
                                          justifyContent: 'center',
                                          bgcolor: 'grey.300',
                                       }}
                                    >
                                       <ImageIcon sx={{ fontSize: 32, color: 'grey.500' }} />
                                    </Box>
                                 )}
                                 <Box sx={{ position: 'absolute', top: 4, right: 4 }}>
                                    <IconButton
                                       size="small"
                                       onClick={(e) => {
                                          e.stopPropagation();
                                          handleDownloadPhoto(photo.id, photo.title, photo.albumId);
                                       }}
                                       disabled={downloadingPhotoId === photo.id}
                                       sx={{
                                          bgcolor: 'rgba(0, 196, 167, 0.9)',
                                          color: 'white',
                                          padding: '4px',
                                          '&:hover': { bgcolor: '#00C4A7' },
                                          '&:disabled': { bgcolor: 'rgba(0, 196, 167, 0.5)' },
                                       }}
                                    >
                                       {downloadingPhotoId === photo.id ? (
                                          <CircularProgress size={16} sx={{ color: 'white' }} />
                                       ) : (
                                          <DownloadIcon sx={{ fontSize: 16 }} />
                                       )}
                                    </IconButton>
                                 </Box>
                              </Box>
                           ))}
                           {Array.from({ length: Math.max(0, 4 - user.photos.length) }).map((_, idx) => (
                              <Box
                                 key={`empty-${idx}`}
                                 sx={{
                                    width: '100%',
                                    height: '100%',
                                    bgcolor: 'grey.200',
                                    borderRadius: 1.5,
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                 }}
                              >
                                 <ImageIcon sx={{ fontSize: 36, color: 'grey.400' }} />
                              </Box>
                           ))}
                        </Box>
                        <CardContent sx={{ flexGrow: 1, p: 2.5 }}>
                           <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                              <Avatar
                                 src={user.photoURL || undefined}
                                 sx={{ width: 28, height: 28, bgcolor: 'primary.main' }}
                              >
                                 {user.displayName.charAt(0).toUpperCase()}
                              </Avatar>
                              <Typography
                                 variant="h6"
                                 sx={{
                                    fontWeight: 700,
                                    fontSize: '1.1rem',
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    WebkitLineClamp: 1,
                                    color: 'text.primary',
                                 }}
                              >
                                 {user.displayName}
                              </Typography>
                           </Box>
                           <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8, color: 'text.secondary' }}>
                              <DownloadIcon sx={{ fontSize: 18, color: 'primary.main' }} />
                              <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '0.9rem' }}>
                                 {user.photoCount} {user.photoCount === 1 ? 'photo' : 'photos'}
                                 {user.albums.length > 1 ? ` from ${user.albums.length} albums` : ''}
                              </Typography>
                           </Box>
                        </CardContent>
                     </Card>
                  </Box>
               ))
               : downloadsData.albums.map((album) => (
                  <Box key={album.albumId} sx={{ minWidth: 300, maxWidth: 300, flexShrink: 0 }}>
                     <Card sx={cardSx} onClick={() => handleAlbumClick(album)}>
                        <Box
                           sx={{
                              height: 220,
                              display: 'grid',
                              gridTemplateColumns: '1fr 1fr',
                              gridTemplateRows: '1fr 1fr',
                              gap: 1,
                              bgcolor: 'grey.100',
                              p: 1,
                           }}
                        >
                           {album.photos.slice(0, 4).map((photo) => (
                              <Box
                                 key={photo.id}
                                 sx={{
                                    position: 'relative',
                                    width: '100%',
                                    height: '100%',
                                    overflow: 'hidden',
                                    borderRadius: 1.5,
                                 }}
                              >
                                 {photo.thumbnailUrl || photo.url ? (
                                    <img
                                       src={photo.thumbnailUrl || photo.url || ''}
                                       alt={photo.title}
                                       style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                                    />
                                 ) : (
                                    <Box
                                       sx={{
                                          width: '100%',
                                          height: '100%',
                                          display: 'flex',
                                          alignItems: 'center',
                                          justifyContent: 'center',
                                          bgcolor: 'grey.300',
                                       }}
                                    >
                                       <ImageIcon sx={{ fontSize: 32, color: 'grey.500' }} />
                                    </Box>
                                 )}
                                 <Box sx={{ position: 'absolute', top: 4, right: 4 }}>
                                    <IconButton
                                       size="small"
                                       onClick={(e) => {
                                          e.stopPropagation();
                                          handleDownloadPhoto(photo.id, photo.title, album.albumId);
                                       }}
                                       disabled={downloadingPhotoId === photo.id}
                                       sx={{
                                          bgcolor: 'rgba(0, 196, 167, 0.9)',
                                          color: 'white',
                                          padding: '4px',
                                          '&:hover': { bgcolor: '#00C4A7' },
                                          '&:disabled': { bgcolor: 'rgba(0, 196, 167, 0.5)' },
                                       }}
                                    >
                                       {downloadingPhotoId === photo.id ? (
                                          <CircularProgress size={16} sx={{ color: 'white' }} />
                                       ) : (
                                          <DownloadIcon sx={{ fontSize: 16 }} />
                                       )}
                                    </IconButton>
                                 </Box>
                              </Box>
                           ))}
                           {Array.from({ length: Math.max(0, 4 - album.photos.length) }).map((_, index) => (
                              <Box
                                 key={`empty-${index}`}
                                 sx={{
                                    width: '100%',
                                    height: '100%',
                                    bgcolor: 'grey.200',
                                    borderRadius: 1.5,
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                 }}
                              >
                                 <ImageIcon sx={{ fontSize: 36, color: 'grey.400' }} />
                              </Box>
                           ))}
                        </Box>
                        <CardContent sx={{ flexGrow: 1, p: 2.5 }}>
                           <Typography
                              variant="h6"
                              sx={{
                                 fontWeight: 700,
                                 fontSize: '1.1rem',
                                 mb: 1.5,
                                 overflow: 'hidden',
                                 textOverflow: 'ellipsis',
                                 WebkitLineClamp: 1,
                                 color: 'text.primary',
                              }}
                           >
                              {album.albumName}
                           </Typography>
                           <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8, color: 'text.secondary' }}>
                              <DownloadIcon sx={{ fontSize: 18, color: 'primary.main' }} />
                              <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '0.9rem' }}>
                                 {album.photoCount} {album.photoCount === 1 ? 'photo' : 'photos'}
                              </Typography>
                           </Box>
                        </CardContent>
                     </Card>
                  </Box>
               ))}
         </Box>

         {/* Photo Modal: by user ("Photos from Name") or by album */}
         <Dialog
            open={openModal}
            onClose={handleCloseModal}
            maxWidth="lg"
            fullWidth
            fullScreen={isMobile}
            PaperProps={{
               sx: {
                  maxHeight: isMobile ? '100vh' : '90vh',
                  m: isMobile ? 0 : 2,
               },
            }}
         >
            <DialogTitle
               sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  pb: 1,
                  px: { xs: 2, sm: 3 },
                  py: { xs: 1.5, sm: 2 },
               }}
            >
               <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 1, sm: 2 }, flex: 1, minWidth: 0 }}>
                  {selectedUser ? (
                     <>
                        <Avatar
                           src={selectedUser.photoURL || undefined}
                           sx={{ width: 40, height: 40, bgcolor: 'primary.main' }}
                        >
                           {selectedUser.displayName.charAt(0).toUpperCase()}
                        </Avatar>
                        <Box sx={{ minWidth: 0, flex: 1 }}>
                           <Typography
                              variant={isMobile ? 'h6' : 'h5'}
                              fontWeight="bold"
                              sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                           >
                              Photos from {selectedUser.displayName}
                           </Typography>
                           <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                              {selectedUser.photoCount} photo{selectedUser.photoCount !== 1 ? 's' : ''}
                           </Typography>
                        </Box>
                     </>
                  ) : (
                     <>
                        <FolderIcon sx={{ color: 'primary.main', fontSize: { xs: 20, sm: 24 } }} />
                        <Box sx={{ minWidth: 0, flex: 1 }}>
                           <Typography
                              variant={isMobile ? 'h6' : 'h5'}
                              fontWeight="bold"
                              sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                           >
                              {selectedAlbum?.albumName}
                           </Typography>
                           <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                              {selectedAlbum?.photoCount ?? 0} photo{(selectedAlbum?.photoCount ?? 0) !== 1 ? 's' : ''}
                           </Typography>
                        </Box>
                     </>
                  )}
               </Box>
               <IconButton onClick={handleCloseModal} edge="end" sx={{ ml: 1 }}>
                  <CloseIcon />
               </IconButton>
            </DialogTitle>
            <DialogContent
               dividers
               sx={{ px: { xs: 1.5, sm: 2, md: 3 }, py: { xs: 2, sm: 3 } }}
            >
               {selectedUser && selectedUser.photos.length > 0 ? (
                  <ImageList cols={isMobile ? 2 : isTablet ? 3 : 4} gap={isMobile ? 8 : 12} sx={{ mb: 0 }}>
                     {selectedUser.photos.map((photo) => (
                        <ImageListItem key={photo.id}>
                           <Box
                              sx={{
                                 position: 'relative',
                                 width: '100%',
                                 paddingTop: '100%',
                                 overflow: 'hidden',
                                 borderRadius: 2,
                                 bgcolor: 'grey.200',
                              }}
                           >
                              <img
                                 src={photo.thumbnailUrl || photo.url || ''}
                                 alt={photo.title}
                                 loading="lazy"
                                 style={{
                                    position: 'absolute',
                                    top: 0,
                                    left: 0,
                                    width: '100%',
                                    height: '100%',
                                    objectFit: 'cover',
                                 }}
                              />
                              <Box sx={{ position: 'absolute', top: { xs: 4, sm: 8 }, right: { xs: 4, sm: 8 } }}>
                                 <IconButton
                                    onClick={(e) => {
                                       e.stopPropagation();
                                       handleDownloadPhoto(photo.id, photo.title, photo.albumId);
                                    }}
                                    disabled={downloadingPhotoId === photo.id}
                                    size={isMobile ? 'small' : 'medium'}
                                    sx={{
                                       bgcolor: 'rgba(0, 196, 167, 0.9)',
                                       color: 'white',
                                       '&:hover': { bgcolor: '#00C4A7' },
                                       '&:disabled': { bgcolor: 'rgba(0, 196, 167, 0.5)' },
                                    }}
                                 >
                                    {downloadingPhotoId === photo.id ? (
                                       <CircularProgress size={isMobile ? 16 : 20} sx={{ color: 'white' }} />
                                    ) : (
                                       <DownloadIcon fontSize="small" />
                                    )}
                                 </IconButton>
                              </Box>
                              <Box
                                 sx={{
                                    position: 'absolute',
                                    bottom: 0,
                                    left: 0,
                                    right: 0,
                                    bgcolor: 'rgba(0,0,0,0.7)',
                                    color: 'white',
                                    p: { xs: 0.75, sm: 1 },
                                    borderBottomLeftRadius: 8,
                                    borderBottomRightRadius: 8,
                                 }}
                              >
                                 <Typography variant="caption" noWrap display="block" sx={{ fontSize: { xs: '0.7rem', sm: '0.75rem' } }}>
                                    {photo.title}
                                 </Typography>
                                 <Typography variant="caption" display="block" sx={{ fontSize: { xs: '0.6rem', sm: '0.65rem' }, opacity: 0.8 }}>
                                    {formatDate(photo.downloadedAt)}
                                 </Typography>
                              </Box>
                           </Box>
                        </ImageListItem>
                     ))}
                  </ImageList>
               ) : selectedAlbum && selectedAlbum.photos && selectedAlbum.photos.length > 0 ? (
                  <ImageList cols={isMobile ? 2 : isTablet ? 3 : 4} gap={isMobile ? 8 : 12} sx={{ mb: 0 }}>
                     {selectedAlbum.photos.map((photo) => (
                           <ImageListItem key={photo.id}>
                           <Box
                              sx={{
                                 position: 'relative',
                                 width: '100%',
                                 paddingTop: '100%',
                                 overflow: 'hidden',
                                 borderRadius: 2,
                                 bgcolor: 'grey.200',
                              }}
                           >
                              <img
                                 src={photo.thumbnailUrl || photo.url || ''}
                                 alt={photo.title}
                                 loading="lazy"
                                 style={{
                                    position: 'absolute',
                                    top: 0,
                                    left: 0,
                                    width: '100%',
                                    height: '100%',
                                    objectFit: 'cover',
                                 }}
                              />
                              {/* Download Button - Always Visible */}
                              <Box
                                 sx={{
                                    position: 'absolute',
                                    top: { xs: 4, sm: 8 },
                                    right: { xs: 4, sm: 8 },
                                 }}
                              >
                                 <IconButton
                                    onClick={(e) => {
                                       e.stopPropagation();
                                       handleDownloadPhoto(photo.id, photo.title, selectedAlbum.albumId);
                                    }}
                                    disabled={downloadingPhotoId === photo.id}
                                    size={isMobile ? 'small' : 'medium'}
                                    sx={{
                                       bgcolor: 'rgba(0, 196, 167, 0.9)',
                                       color: 'white',
                                       '&:hover': {
                                          bgcolor: '#00C4A7',
                                       },
                                       '&:disabled': {
                                          bgcolor: 'rgba(0, 196, 167, 0.5)',
                                       },
                                    }}
                                 >
                                    {downloadingPhotoId === photo.id ? (
                                       <CircularProgress size={isMobile ? 16 : 20} sx={{ color: 'white' }} />
                                    ) : (
                                       <DownloadIcon fontSize="small" />
                                    )}
                                 </IconButton>
                              </Box>
                              <Box
                                 sx={{
                                    position: 'absolute',
                                    bottom: 0,
                                    left: 0,
                                    right: 0,
                                    bgcolor: 'rgba(0,0,0,0.7)',
                                    color: 'white',
                                    p: { xs: 0.75, sm: 1 },
                                    borderBottomLeftRadius: 8,
                                    borderBottomRightRadius: 8,
                                 }}
                              >
                                 <Typography 
                                    variant="caption" 
                                    noWrap 
                                    display="block"
                                    sx={{ fontSize: { xs: '0.7rem', sm: '0.75rem' } }}
                                 >
                                    {photo.title}
                                 </Typography>
                                 <Typography 
                                    variant="caption" 
                                    display="block" 
                                    sx={{ 
                                       fontSize: { xs: '0.6rem', sm: '0.65rem' },
                                       opacity: 0.8,
                                    }}
                                 >
                                    {formatDate(photo.downloadedAt)}
                                 </Typography>
                              </Box>
                              </Box>
                           </ImageListItem>
                        ))}
                     </ImageList>
               ) : (
                  <Box sx={{ textAlign: 'center', py: 4 }}>
                     <ImageIcon sx={{ fontSize: 64, color: 'grey.400', mb: 2 }} />
                     <Typography variant="body1" color="text.secondary">
                        {selectedUser ? 'No photos' : 'No photos in this album'}
                     </Typography>
                  </Box>
               )}
            </DialogContent>
         </Dialog>

         <Snackbar
            open={snackbarOpen}
            autoHideDuration={6000}
            onClose={() => setSnackbarOpen(false)}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
         >
            <Alert severity={snackbarSeverity} onClose={() => setSnackbarOpen(false)}>
               {snackbarMessage}
            </Alert>
         </Snackbar>
      </Box>
   );
};

export default DownloadAnalytics;

